export class AdaptiveQuiz {
    constructor(quizId, studentId, questions, setAbility, setDifficulty, maxQuestions) {
        this.quizId = quizId;
        this.studentId = studentId;
        this.correctAnswersCount = 0;
        this.incorrectAnswersCount = 0;
        this.ability = 1; // Initial student ability is 0
        this.difficultyHistory = [];
        this.usedQuestions = [];
        this.answers = [];
        this.result = [];
        this.questionPool = [];
        this.totalLogitScore = 0;
        this.accuracy = 0;
        this.maxQuestions = maxQuestions;
        let i = 0;
        this.setAbility = setAbility;
        this.setDifficulty = setDifficulty;
        questions.forEach((question, index) => {
            let valueToAdd = 0;
            switch (question.difficulty_level.name) {
                case "Very Easy":
                    valueToAdd = 1;
                    break;
                case "Easy":
                    valueToAdd = 2;
                    break;
                case "Medium":
                    valueToAdd = 3;
                    break;
                case "Difficult":
                    valueToAdd = 4;
                    break;
                case "Very Difficult":
                    valueToAdd = 5;
                    break;
                default:
                    valueToAdd = 0;
                    console.log("No Difficulty Lever Found!")
                    break;
            }
            question.difficulty = valueToAdd;
            question.no = i++;
            // Add valueToAdd to the result array with task details
            this.questionPool.push(question);
            // result.push({ id: question.no, valueToAdd });
        });
        this.currentQuestion = null;
        this.difficultyRange = 1; // Initial difficulty range
        this.initialDifficultyRange = 1; // Store initial difficulty range
    }

    startQuiz() {
        this.selectNextQuestion();
    }

    recordAnswer(isCorrect) {
        const probabilityCorrect = 1 / (1 + Math.exp(-(this.ability - this.currentQuestion.difficulty)));
        this.difficultyHistory.push({
            ability: this.ability,
            difficulty: this.currentQuestion.difficulty,
            isCorrect: isCorrect
        });


        // Update student ability based on answer
        if (isCorrect) {
            this.totalLogitScore = this.totalLogitScore + this.ability * 1; // Add to totalLogitScore
            this.ability += (1 - probabilityCorrect);  // Increase ability if correct
            this.correctAnswersCount++;
        } else {
            this.totalLogitScore = this.totalLogitScore + this.ability * -1;
            this.ability -= probabilityCorrect;  // Decrease ability if incorrect
            this.incorrectAnswersCount++;
        }
        this.setAbility(this.ability);

        this.answers.push({ question: this.currentQuestion, correct: isCorrect });


        this.accuracy = this.correctAnswersCount / (this.correctAnswersCount + this.incorrectAnswersCount);
        // Select next question based on the updated ability
        if (this.incorrectAnswersCount + this.correctAnswersCount <= this.maxQuestions) {
            this.selectNextQuestion();

        }


        return {
            currentAbility: this.ability.toFixed(2),
            correctCount: this.correctAnswersCount,
            incorrectCount: this.incorrectAnswersCount,
            difficultyHistory: this.difficultyHistory,
            accuracy: this.accuracy
        };
    }

    getFinalResult() {
        return {
            ability: this.ability.toFixed(2),
            correctCount: this.correctAnswersCount,
            incorrectCount: this.incorrectAnswersCount,
            difficultyHistory: this.difficultyHistory,
            totalLogitScore: this.totalLogitScore,
            accuracy: this.accuracy
        };
    }

    selectNextQuestion() {
        let possibleQuestions = this.getQuestionsInRange(this.ability, this.difficultyRange);

        // If no questions available, expand the range in both directions until we find a question
        while (possibleQuestions.length === 0 && this.difficultyRange < 5) {
            console.log("Expanding difficulty range...");
            this.difficultyRange += 1; // Expand the range further
            possibleQuestions = this.getQuestionsInRange(this.ability, this.difficultyRange);
        }

        // If no questions found and range exceeds the limit, alert the user
        if (possibleQuestions.length === 0) {
            console.error("No more questions available in the current difficulty range.");
            return;
        }

        // Randomly select a question from the possible questions
        const nextQuestion = possibleQuestions[Math.floor(Math.random() * possibleQuestions.length)];
        // Mark the question as used
        this.usedQuestions.push(nextQuestion.no);
        this.currentQuestion = nextQuestion;
        this.setDifficulty(this.currentQuestion.difficulty);


        // Log the selected question number to the console
        console.log(`Selected Question ID: ${this.currentQuestion.no}, Difficulty: ${this.currentQuestion.difficulty}`);

        // After selecting a question, reset the difficulty range to its initial value
        this.difficultyRange = this.initialDifficultyRange;

    }

    getQuestionsInRange(currentAbility, range) {
        let localability = currentAbility;
        if (localability > 5) {
            localability = 5;
        }

        return this.questionPool.filter(q => {
            return !this.usedQuestions.includes(q.no) &&
                q.difficulty >= Math.max(localability - range, 0) &&  // Ensure lower bound is at least 0
                q.difficulty <= Math.min(localability + range, 5);   // Ensure upper bound is at most 5
        });
    }

    getCurrentDifficulty() {
        return this.currentQuestion.difficulty;
    }

    getTotalLogitSCore() {
        return this.totalLogitScore;
    }

    getCurrentAbility() {
        return this.ability;
    }
    getAccuracy() {
        return this.accuracy;
    }

    static comparePerformance(quizA, quizB) {
        // Compare by total logit score
        if (quizA.totalLogitScore !== quizB.totalLogitScore) {
            return quizA.totalLogitScore - quizB.totalLogitScore;
        }


        if (quizA.accuracy !== quizB.accuracy) {
            return quizA.accuracy - quizB.accuracy;
        }

        // If both are the same, compare by ability score
        if (quizA.ability !== quizB.ability) {
            return quizA.ability - quizB.ability;
        }

        // If all metrics are the same, consider them equal
        return 0;
    }

    static sorterFunction(quizA, quizB) {
        // Compare by total logit score (higher is better)
        if (quizA.totalLogitScore < quizB.totalLogitScore) {
            return 1;  // quizB is better
        }
        if (quizA.totalLogitScore > quizB.totalLogitScore) {
            return -1; // quizA is better
        }

        // If logit scores are the same, compare by accuracy (higher is better)
        const accuracyA = quizA.correctAnswersCount / (quizA.correctAnswersCount + quizA.incorrectAnswersCount);
        const accuracyB = quizB.correctAnswersCount / (quizB.correctAnswersCount + quizB.incorrectAnswersCount);

        if (accuracyA < accuracyB) {
            return 1;  // quizB is better
        }
        if (accuracyA > accuracyB) {
            return -1; // quizA is better
        }

        // If both logit score and accuracy are the same, compare by ability score (higher is better)
        if (quizA.ability < quizB.ability) {
            return 1;  // quizB is better
        }
        if (quizA.ability > quizB.ability) {
            return -1; // quizA is better
        }

        // If all metrics are the same, consider them equal
        return 0; // Both are equal
    }
}

// const questionPool = [];
// // Generate 100 questions with varying difficulty from 0.1 to 5.0
// for (let i = 1; i <= 100; i++) {
//     const difficulty = (i % 5 === 0) ? (Math.random() * 4 + 1) : (Math.random() * 0.5 + 0.5);
//     questionPool.push({ id: i, difficulty: parseFloat(difficulty.toFixed(2)) });
// }

// const quiz = new AdaptiveQuiz('quiz123', 'student456', questionPool);

// quiz.startQuiz();

// let abilityChart;
// let difficultyChart;

// Initialize Ability Chart with cubic interpolation
// function initAbilityChart() {
//     const ctx = document.getElementById('abilityChart').getContext('2d');
//     abilityChart = new Chart(ctx, {
//         type: 'line',
//         data: {
//             labels: [],
//             datasets: [{
//                 label: 'Ability (θ)',
//                 data: [],
//                 borderColor: 'rgba(75, 192, 192, 1)',
//                 fill: false,
//                 cubicInterpolationMode: 'monotone'  // Make the line smooth and continuous
//             }]
//         },
//         options: {
//             responsive: true,
//             maintainAspectRatio: false,
//             scales: {
//                 y: {
//                     beginAtZero: true
//                 }
//             }
//         }
//     });
// }

// Initialize Difficulty Chart with cubic interpolation
// function initDifficultyChart() {
//     const ctx = document.getElementById('difficultyChart').getContext('2d');
//     difficultyChart = new Chart(ctx, {
//         type: 'line',
//         data: {
//             labels: [],
//             datasets: [{
//                 label: 'Difficulty (β)',
//                 data: [],
//                 borderColor: 'rgba(255, 99, 132, 1)',
//                 fill: false,
//                 cubicInterpolationMode: 'monotone'  // Make the line smooth and continuous
//             }]
//         },
//         options: {
//             responsive: true,
//             maintainAspectRatio: false,
//             scales: {
//                 y: {
//                     beginAtZero: true
//                 }
//             }
//         }
//     });
// }

// Function to update the charts
// function updateUI(result) {
//     const abilityData = result.difficultyHistory.map(item => item.ability);
//     const difficultyData = result.difficultyHistory.map(item => item.difficulty);

//     // Update Ability Chart
//     abilityChart.data.labels = Array.from({ length: abilityData.length }, (_, i) => `Q${i + 1}`);
//     abilityChart.data.datasets[0].data = abilityData;

//     // Update Difficulty Chart
//     difficultyChart.data.labels = Array.from({ length: difficultyData.length }, (_, i) => `Q${i + 1}`);
//     difficultyChart.data.datasets[0].data = difficultyData;

//     // Re-render the charts
//     abilityChart.update();
//     difficultyChart.update();
// }

// Function to handle submitting answers and displaying results
// function submitAnswer(isCorrect) {
//     const result = quiz.recordAnswer(isCorrect);
//     updateUI(result);
// }

// Add event listeners to the buttons for correct and incorrect answers
// document.getElementById("correctBtn").addEventListener("click", () => submitAnswer(true));
// document.getElementById("incorrectBtn").addEventListener("click", () => submitAnswer(false));

// Initialize charts when the page is loaded
// initAbilityChart();
// initDifficultyChart();
